import { type ReactNode } from 'react';
import { REPORT } from '../print/PrintAtoms';
import { ClusterAssembly, ClusterSite, GridScoreComponent } from '../../lib/api';
import { SANS, SERIF, MONO, RULE, RULE_SOFT, Eyebrow } from './DossierSections';

const TIER_LABEL: Record<GridScoreComponent['tier'], string> = {
  scored: 'SCORED', curated: 'CURATED', neutral: 'NEUTRAL', provisional: 'PROVISIONAL',
};

function Chip({ children }: { children: ReactNode }) {
  return (
    <span style={{
      fontFamily: MONO, fontSize: 9, fontWeight: 600, letterSpacing: '0.08em',
      padding: '1px 6px', borderRadius: 3, color: REPORT.tertiary,
      background: REPORT.sand, border: `1px solid ${RULE}`,
    }}>{children}</span>
  );
}

function ComponentBar({ c }: { c: GridScoreComponent }) {
  const frac = c.max ? Math.max(0, Math.min(1, c.points / c.max)) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <span style={{ fontFamily: SANS, fontSize: 12.5, color: REPORT.body }}>{c.label} <Chip>{TIER_LABEL[c.tier]}</Chip></span>
        <span style={{ fontFamily: SANS, fontSize: 12, color: REPORT.secondary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{c.points.toFixed(1)} / {c.max}</span>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: RULE_SOFT, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${frac * 100}%`, background: REPORT.highlight, borderRadius: 2 }} />
      </div>
    </div>
  );
}

function AssemblyChip({ a }: { a: ClusterAssembly }) {
  return (
    <a href={`/briefing/${a.parcel_id}/v2`} style={{
      flex: '1 1 220px', textDecoration: 'none', color: REPORT.body,
      background: REPORT.white, border: `1px solid ${RULE}`, borderRadius: 5, padding: 16,
    }}>
      <div style={{ fontFamily: MONO, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', color: REPORT.tertiary }}>
        {a.assembly_key === 'core' ? 'Core assembly' : 'Available assembly'}
      </div>
      <div style={{ fontFamily: SERIF, fontSize: 26, color: REPORT.heading, marginTop: 6 }}>
        {a.developable_acres != null ? Math.round(a.developable_acres) : '–'} <span style={{ fontFamily: SANS, fontSize: 12.5, color: REPORT.secondary }}>developable ac</span>
      </div>
      <div style={{ fontFamily: SANS, fontSize: 12, color: REPORT.secondary, marginTop: 3 }}>
        {Math.round(a.acreage)} ac total{a.buildability_pct != null ? ` · ${a.buildability_pct}% buildable` : ''}
      </div>
    </a>
  );
}

const CARD: React.CSSProperties = {
  flex: '1 1 340px', background: REPORT.white, border: `1px solid ${RULE}`, borderRadius: 6, padding: 20,
};

export function ClusterHero({ site, assemblies }: { site: ClusterSite | null; assemblies: ClusterAssembly[] }) {
  const grid = site?.grid ?? null;
  const anchor = grid?.anchor;
  const nodeEcon = grid ? grid.components.filter((c) => c.key === 'utility' || c.key === 'price' || c.key === 'incentives') : [];
  const orderedAssemblies = (['core', 'available'] as const)
    .map((k) => assemblies.find((a) => a.assembly_key === k))
    .filter((a): a is ClusterAssembly => a != null);

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        {/* Grid-power anchor — the "why this site at all", shown once */}
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
            <Eyebrow color={REPORT.heading}>Grid power · site anchor</Eyebrow>
            {site && <Chip>{site.dataConfidence.toUpperCase()} DATA</Chip>}
          </div>
          {anchor && (
            <div style={{ marginTop: 12, fontFamily: SANS, fontSize: 13.5, color: REPORT.body, lineHeight: 1.5 }}>
              <div>Interconnection anchor: <strong style={{ color: REPORT.heading }}>{anchor.kv != null ? `${anchor.kv} kV` : '—'}</strong>{anchor.miles != null ? ` @ ${anchor.miles.toFixed(1)} mi` : ''}{anchor.lines != null ? ` · ${anchor.lines} lines` : ''}</div>
              {anchor.backbone_345kv && (
                <div style={{ color: REPORT.secondary, fontSize: 12.5 }}>HV backbone tap: {anchor.backbone_345kv.kv} kV{anchor.backbone_345kv.miles != null ? ` @ ${anchor.backbone_345kv.miles.toFixed(1)} mi` : ''}</div>
              )}
            </div>
          )}
          {grid && (
            <div style={{ marginTop: 8, fontFamily: SANS, fontSize: 12.5, color: REPORT.secondary }}>
              Grid-power score: <strong style={{ color: REPORT.heading }}>{grid.total}</strong> / {grid.max} — site-constant across all tracts; it sets the ceiling, it does not rank tracts.
            </div>
          )}
          {site && site.improvementHooks.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontFamily: MONO, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', color: REPORT.tertiary, marginBottom: 5 }}>What would sharpen this estimate</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontFamily: SANS, fontSize: 12, color: REPORT.secondary, lineHeight: 1.5 }}>
                {site.improvementHooks.map((h, i) => <li key={i} style={{ marginBottom: 2 }}>{h}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Node economics — curated multipliers */}
        {nodeEcon.length > 0 && (
          <div style={CARD}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <Eyebrow color={REPORT.heading}>Node economics</Eyebrow>
              {site?.node?.display_name && <span style={{ fontFamily: SANS, fontSize: 12, color: REPORT.secondary }}>{site.node.display_name}</span>}
            </div>
            <div style={{ marginTop: 14 }}>
              {nodeEcon.map((c) => <ComponentBar key={c.key} c={c} />)}
            </div>
          </div>
        )}
      </div>

      {/* Assembly summary — the two "assemble these" headlines, by developable acres */}
      {orderedAssemblies.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
          {orderedAssemblies.map((a) => <AssemblyChip key={a.assembly_key} a={a} />)}
        </div>
      )}
    </div>
  );
}
